/**
 * Mic-capture haptic + visual confirmation pulses for the closed-eyes
 * prayer scenario. Three discrete moments fire a pulse:
 *
 *   - pulseStart()       — recorder is up, we're listening
 *   - pulseFirstVoice()  — analyser detected the user's first sustained
 *                          input (only fires in enhanced mode where the
 *                          analyser is wired)
 *   - pulseStop()        — user tapped stop, we got it
 *
 * Each pulse drives TWO outputs:
 *   1. navigator.vibrate(...) for tactile feedback
 *   2. A window CustomEvent('adia:mic-flash') for a visual pulse on the
 *      active mic glyph
 *
 * iOS Safari note (load-bearing): navigator.vibrate is a no-op on every
 * iOS Safari build to date — including PWA mode. There is no Web API
 * that produces tactile feedback on iOS Safari without a native bridge.
 * The closed-eyes trust mechanism on iOS is therefore the meter's idle
 * pulse plus the visual flash dispatched here. Android (Chrome, Samsung
 * Internet, etc.) honors navigator.vibrate so the haptic IS additive on
 * those devices. Do not rely on the haptic firing on iOS; do not gate
 * trust-critical UX on it.
 */

const FLASH_EVENT = 'adia:mic-flash';

export type MicFlashKind = 'start' | 'first-voice' | 'stop';

export interface MicFlashEventDetail {
  kind: MicFlashKind;
}

export const MIC_FLASH_EVENT = FLASH_EVENT;

function dispatchFlash(kind: MicFlashKind): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent<MicFlashEventDetail>(FLASH_EVENT, { detail: { kind } }),
    );
  } catch {
    // Older browsers without CustomEvent constructor — silently skip.
  }
}

function vibrate(pattern: number | number[]): void {
  if (typeof navigator === 'undefined') return;
  if (typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // Some engines throw if no user gesture has primed the vibration
    // permission. We fire from gesture-adjacent paths but the catch
    // keeps a stray throw from breaking the recording flow.
  }
}

export function pulseStart(): void {
  vibrate(40);
  dispatchFlash('start');
}

export function pulseFirstVoice(): void {
  vibrate(25);
  dispatchFlash('first-voice');
}

export function pulseStop(): void {
  vibrate(40);
  dispatchFlash('stop');
}
